import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { isGalleryStudioPlanSlotReady } from "@nova-ai/shared/gallery-studio"

import {
  useGalleryDraftJob,
  useGalleryStudioActions,
  type GalleryDraftIdentity,
  type GalleryDraftPreview,
  type GalleryStudioPlanInputs,
  type GalleryStudioPlanSlot,
} from "../services/use-gallery-studio"

type GalleryStudioStep = "plan" | "review" | "results"

export type GalleryStudioEditorState = {
  editors: Record<string, GalleryStudioPlanSlot>
  pristineEditors: Record<string, GalleryStudioPlanSlot>
  planVersion: number
}

export function adoptGalleryStudioRemoteEditors(
  current: GalleryStudioEditorState,
  remoteEditors: Record<string, GalleryStudioPlanSlot>,
  remoteVersion: number
): GalleryStudioEditorState {
  if (
    remoteVersion <= current.planVersion ||
    editorFingerprint(current.editors) !== editorFingerprint(current.pristineEditors)
  ) {
    return current
  }
  return {
    editors: remoteEditors,
    pristineEditors: remoteEditors,
    planVersion: remoteVersion,
  }
}

export function markGalleryStudioEditorsSaved(
  current: GalleryStudioEditorState,
  savedEditors: Record<string, GalleryStudioPlanSlot>,
  savedVersion: number
): GalleryStudioEditorState {
  return { ...current, pristineEditors: savedEditors, planVersion: savedVersion }
}

export function mergeGalleryStudioAddedSlotEditors(
  current: GalleryStudioEditorState,
  remoteEditors: Record<string, GalleryStudioPlanSlot>
): GalleryStudioEditorState {
  const addedEditors = Object.fromEntries(
    Object.entries(remoteEditors).filter(([slotId]) => current.editors[slotId] === undefined)
  )
  if (Object.keys(addedEditors).length === 0) {
    return current
  }
  return {
    ...current,
    editors: { ...current.editors, ...addedEditors },
    pristineEditors: { ...current.pristineEditors, ...addedEditors },
  }
}

export function galleryDraftPreviewSelectionMatches(
  preview: GalleryDraftPreview,
  regenerateItems: GalleryDraftIdentity[]
): boolean {
  const selected = new Set(regenerateItems.map(identityKey))
  const reviewed = new Set(
    preview.items
      .filter((item) => item.regenerateExisting)
      .map((item) =>
        identityKey({
          slotId: item.slotId,
          simpleSku: item.simpleSku,
          marketplace: item.marketplace,
          format: item.format,
        })
      )
  )
  return selected.size === reviewed.size && [...selected].every((key) => reviewed.has(key))
}

export function useGalleryStudioWorkspace(inputs: GalleryStudioPlanInputs) {
  const secondarySlots = useMemo(
    () => inputs.intent.slots.filter((slot) => slot.kind === "secondary" && slot.isActive),
    [inputs.intent.slots]
  )
  const [editorState, setEditorState] = useState<GalleryStudioEditorState>(() => {
    const editors = initialEditors(inputs)
    return {
      editors,
      pristineEditors: editors,
      planVersion: inputs.latestPlan?.version ?? 0,
    }
  })
  const { editors, planVersion } = editorState
  const [activeSlotId, setActiveSlotId] = useState(
    secondarySlots[0]?.id ?? inputs.intent.slots.find((slot) => slot.kind === "main")?.id ?? ""
  )
  const [step, setStep] = useState<GalleryStudioStep>("plan")
  const [preview, setPreview] = useState<GalleryDraftPreview | null>(null)
  const [regenerateItems, setRegenerateItems] = useState<GalleryDraftIdentity[]>([])
  const [jobId, setJobId] = useState("")
  const [validationError, setValidationError] = useState<string | null>(null)
  const [pendingAddedSlotId, setPendingAddedSlotId] = useState<string | null>(null)
  const actions = useGalleryStudioActions(inputs.configSku)
  const job = useGalleryDraftJob(inputs.configSku, jobId)

  useEffect(() => {
    const remoteEditors = initialEditors(inputs)
    setEditorState((current) => {
      const adopted = adoptGalleryStudioRemoteEditors(
        current,
        remoteEditors,
        inputs.latestPlan?.version ?? 0
      )
      return mergeGalleryStudioAddedSlotEditors(adopted, remoteEditors)
    })
  }, [inputs])

  const markSlotNavigationIntent = useSlotScrollSpy(
    inputs.intent.slots.filter((slot) => slot.isActive).map((slot) => slot.id),
    setActiveSlotId
  )

  const updateEditor = useCallback((next: GalleryStudioPlanSlot) => {
    setEditorState((current) => ({
      ...current,
      editors: { ...current.editors, [next.slotId]: next },
    }))
  }, [])

  const activateSlot = useCallback(
    (slotId: string) => {
      markSlotNavigationIntent()
      setActiveSlotId(slotId)
      globalThis.document
        ?.getElementById(`gallery-slot-${slotId}`)
        ?.scrollIntoView?.({ behavior: "smooth", block: "start" })
    },
    [markSlotNavigationIntent]
  )

  useEffect(() => {
    if (
      !pendingAddedSlotId ||
      !inputs.intent.slots.some(
        (slot) => slot.id === pendingAddedSlotId && slot.kind === "secondary" && slot.isActive
      )
    ) {
      return
    }
    activateSlot(pendingAddedSlotId)
    setPendingAddedSlotId(null)
  }, [activateSlot, inputs.intent.slots, pendingAddedSlotId])

  const addSecondarySlot = useCallback(
    async (name: string) => {
      const slotId = crypto.randomUUID()
      setPendingAddedSlotId(slotId)
      try {
        await actions.updateSlots.mutateAsync({
          expectedVersion: inputs.intent.slotPlanVersion,
          secondarySlots: [
            ...secondarySlots
              .toSorted((left, right) => (left.position ?? 999) - (right.position ?? 999))
              .map((slot) => ({ id: slot.id, name: slot.name })),
            { id: slotId, name },
          ],
        })
      } catch (cause) {
        setPendingAddedSlotId(null)
        throw cause
      }
      await actions.rereadSlotPlan.mutateAsync()
    },
    [actions.rereadSlotPlan, actions.updateSlots, inputs.intent.slotPlanVersion, secondarySlots]
  )

  const rereadSlotPlan = useCallback(async () => {
    await actions.rereadSlotPlan.mutateAsync()
    actions.updateSlots.reset()
  }, [actions.rereadSlotPlan, actions.updateSlots])

  const reviewGeneration = useCallback(async () => {
    const plannedSlots = secondarySlots.map((slot) => editors[slot.id]).filter(isPlanSlot)
    const invalid = validatePlan(plannedSlots, secondarySlots.length, inputs)
    if (invalid) {
      setValidationError(invalid)
      return
    }
    setValidationError(null)
    const saved = await actions.savePlan.mutateAsync({
      expectedVersion: planVersion,
      secondarySlots: plannedSlots,
    })
    const savedEditors = Object.fromEntries(plannedSlots.map((slot) => [slot.slotId, slot]))
    setEditorState((current) => markGalleryStudioEditorsSaved(current, savedEditors, saved.version))
    const nextPreview = await actions.preview.mutateAsync({
      expectedPlanVersion: saved.version,
      regenerateItems: [],
    })
    setRegenerateItems([])
    setPreview(nextPreview)
    setStep("review")
  }, [actions.preview, actions.savePlan, editors, inputs, planVersion, secondarySlots])

  const toggleRegeneration = useCallback(
    async (identity: GalleryDraftIdentity) => {
      const key = identityKey(identity)
      const next = regenerateItems.some((item) => identityKey(item) === key)
        ? regenerateItems.filter((item) => identityKey(item) !== key)
        : [...regenerateItems, identity]
      let nextPreview: GalleryDraftPreview
      try {
        nextPreview = await actions.preview.mutateAsync({
          expectedPlanVersion: planVersion,
          regenerateItems: next,
        })
      } catch {
        return
      }
      setRegenerateItems(next)
      setPreview(nextPreview)
    },
    [actions.preview, planVersion, regenerateItems]
  )

  const launchGeneration = useCallback(async () => {
    if (
      !preview ||
      actions.preview.error ||
      !galleryDraftPreviewSelectionMatches(preview, regenerateItems)
    ) {
      return
    }
    const started = await actions.launch.mutateAsync({
      previewId: preview.previewId,
      previewHash: preview.previewHash,
    })
    setJobId(started.id)
    setStep("results")
  }, [actions.launch, actions.preview.error, preview, regenerateItems])

  const cancelGeneration = useCallback(async () => {
    if (!jobId) {
      return
    }
    await actions.cancel.mutateAsync(jobId)
    await job.refetch()
  }, [actions.cancel, job, jobId])

  const reviewPaidRegeneration = useCallback(
    async (identity: GalleryDraftIdentity) => {
      const nextPreview = await actions.preview.mutateAsync({
        expectedPlanVersion: planVersion,
        regenerateItems: [identity],
      })
      setRegenerateItems([identity])
      setPreview(nextPreview)
      setStep("review")
    },
    [actions.preview, planVersion]
  )

  const rejectScene = useCallback(
    async (input: Parameters<typeof actions.rejectScene.mutateAsync>[0]) => {
      await actions.rejectScene.mutateAsync(input)
      await job.refetch()
    },
    [actions, job]
  )

  return {
    secondarySlots,
    editors,
    activeSlotId,
    step,
    preview,
    regenerateItems,
    previewSelectionMatches: preview
      ? galleryDraftPreviewSelectionMatches(preview, regenerateItems)
      : false,
    job,
    validationError,
    hasPendingSlotReadback: pendingAddedSlotId !== null,
    addSecondarySlot,
    rereadSlotPlan,
    actions,
    updateEditor,
    activateSlot,
    reviewGeneration,
    toggleRegeneration,
    launchGeneration,
    cancelGeneration,
    reviewPaidRegeneration,
    rejectScene,
    setStep,
  }
}

function editorFingerprint(editors: Record<string, GalleryStudioPlanSlot>): string {
  return JSON.stringify(
    Object.entries(editors).toSorted(([left], [right]) => left.localeCompare(right))
  )
}

function initialEditors(inputs: GalleryStudioPlanInputs): Record<string, GalleryStudioPlanSlot> {
  const saved = new Map(inputs.latestPlan?.secondarySlots.map((slot) => [slot.slotId, slot]) ?? [])
  const defaultMarketplace = inputs.marketplaces.includes("shop-apotheke-it")
    ? "shop-apotheke-it"
    : (inputs.marketplaces[0] ?? "")
  return Object.fromEntries(
    inputs.intent.slots
      .filter((slot) => slot.kind === "secondary" && slot.isActive)
      .map((slot) => [
        slot.id,
        saved.get(slot.id) ?? {
          slotId: slot.id,
          name: slot.name,
          brief: "",
          copy: { headline: "", subheadline: "", benefit: "", trust: "" },
          visualDirectionReferenceAssetIds: [],
          marketplaces: defaultMarketplace ? [defaultMarketplace] : [],
          formats: ["square"],
          simpleSkus: inputs.intent.targetSimpleSkus,
          masterSimpleSku: inputs.intent.targetSimpleSkus[0] ?? "",
        },
      ])
  )
}

function validatePlan(
  plannedSlots: GalleryStudioPlanSlot[],
  expectedCount: number,
  inputs: GalleryStudioPlanInputs
): string | null {
  if (plannedSlots.length !== expectedCount) {
    return "Every active secondary Slot needs plan settings."
  }
  for (const slot of plannedSlots) {
    if (!isGalleryStudioPlanSlotReady(slot, inputs)) {
      return `${slot.name} needs complete copy, approved identities, usable Visual Direction references, and valid marketplace contracts.`
    }
  }
  return null
}

function isPlanSlot(slot: GalleryStudioPlanSlot | undefined): slot is GalleryStudioPlanSlot {
  return slot !== undefined
}

function identityKey(identity: GalleryDraftIdentity): string {
  return `${identity.slotId}|${identity.simpleSku}|${identity.marketplace}|${identity.format}`
}

function useSlotScrollSpy(slotIds: string[], onActive: (slotId: string) => void): () => void {
  const stableIds = slotIds.join("|")
  const hasNavigationIntent = useRef(false)
  const markNavigationIntent = useCallback(() => {
    hasNavigationIntent.current = true
  }, [])
  useEffect(() => {
    const markPointerIntent = () => markNavigationIntent()
    const markKeyboardIntent = (event: KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "].includes(event.key)) {
        markNavigationIntent()
      }
    }
    globalThis.addEventListener?.("wheel", markPointerIntent, { passive: true })
    globalThis.addEventListener?.("touchmove", markPointerIntent, { passive: true })
    globalThis.addEventListener?.("keydown", markKeyboardIntent)
    if (!("IntersectionObserver" in globalThis)) {
      return () => {
        globalThis.removeEventListener?.("wheel", markPointerIntent)
        globalThis.removeEventListener?.("touchmove", markPointerIntent)
        globalThis.removeEventListener?.("keydown", markKeyboardIntent)
      }
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!hasNavigationIntent.current) {
          return
        }
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .toSorted((left, right) => left.boundingClientRect.top - right.boundingClientRect.top)
          .at(0)
        const slotId = visible?.target.getAttribute("data-gallery-slot")
        if (slotId) {
          onActive(slotId)
        }
      },
      { rootMargin: "-112px 0px -65% 0px", threshold: [0, 0.1, 0.5] }
    )
    for (const slotId of stableIds.split("|").filter(Boolean)) {
      const section = document.getElementById(`gallery-slot-${slotId}`)
      if (section) {
        observer.observe(section)
      }
    }
    return () => {
      observer.disconnect()
      globalThis.removeEventListener?.("wheel", markPointerIntent)
      globalThis.removeEventListener?.("touchmove", markPointerIntent)
      globalThis.removeEventListener?.("keydown", markKeyboardIntent)
    }
  }, [markNavigationIntent, onActive, stableIds])
  return markNavigationIntent
}
